import PropTypes from "prop-types";
import React from "react";
import {DefaultButton, Separator, Stack, StackItem, Text, TextField, TooltipHost} from "@fluentui/react";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import * as commonUtils from "../../utils/CommonUtils";
import * as safeActions from "../../services/safe/safe-actions";
import * as cvActions from "../../services/cv/cv-actions";
import * as uiActions from "../../services/ui/ui-actions";
import {useTheme} from "../../services/ui/ui-services";
import {CvDetailsList} from "../widgets/CvDetailsList";
import {CvDropdown} from "../widgets/CvDropdown";
import {CvTextField} from "../widgets/CvTextField";
import * as enums from "../cv/Enums";
import ConfirmDialog from "../ConfirmDialog";
import {createHelpIcon} from "../widgets/CvHelpIcon";

const DEFAULT_ACCOUNT_NAME = "-- NIEUW ACCOUNT --";
const DEFAULT_ACCOUNT_EMAIL = "nieuw.account@cerios.nl";

const Accounts = prps => {

  const selectors = useSelector(
    state => ({
      locale: state.ui.userPrefs.locale,
      authInfo: state.auth.authInfo,
      accountEntity: state.safe.content.account,
      authorizationEntity: state.safe.content.authorization,
      brandEntity: state.safe.content.brand,
      businessUnitEntity: state.safe.content.businessUnit,
      selectedAccountId: state.ui.selectedId.account
    }),
    {equalityFn: shallowEqual}
  );
  const dispatch = useDispatch();
  const dispatches = React.useMemo(() => ({
      setSelectedAccountId: id => dispatch(uiActions.setSelectedId("account", id)),
      deleteAccount: id => dispatch(safeActions.deleteAccount(id)),
      replaceAccount: (id, instance) => dispatch(safeActions.changeInstance("account", id, instance)),
      replaceAuthorization: (id, instance) => dispatch(safeActions.changeInstance("authorization", id, instance)),
      replaceBusinessUnit: (id, instance) => dispatch(safeActions.changeInstance("businessUnit", id, instance)),
      fetchCvByAccountId: accountId => dispatch(cvActions.fetchCvByAccountId(accountId)),
      fetchCvReport: () => dispatch(cvActions.fetchCvReport())
    }),
    [dispatch]);
  const props = {...prps, ...selectors, ...dispatches};

  const combineEntities = (accountEntity, authorizationEntity, brandEntity, businessUnitEntity) => {
    const accounts = Object.values(accountEntity || {})
      .filter(account => account._id) // Don't show deleted accounts.
      .sort((l, r) => commonUtils.comparePrimitives(l.name, r.name));
    const combined = {};
    for (const account of accounts) {
      const authorization = Object.values(authorizationEntity || {})
        .find(authorizationInstance => authorizationInstance.accountId === account._id);

      const businessUnit = Object.values(businessUnitEntity || {})
        .find(businessUnit => businessUnit.accountIds?.includes(account._id));

      combined[account._id] = {
        ...account,
        authorization: authorization,
        brand: brandEntity?.[businessUnit?.brandId],
        businessUnit: businessUnit
      };
    }

    // Export all accounts including their businessUnit to csv.
    // console.log("Export to CSV", "name,email,unit\n" + Object.values(combined)
    //   .map(c => `"${c.name}","${c.email}","${c.businessUnit?.name || ""}"`)
    //   .join("\n"));

    return combined;
  };

  const switchBusinessUnitOfAccount = React.useCallback((accountId, combinedInstance) => {
    const fromBusinessUnit = Object.values(props.businessUnitEntity || {})
      .find(businessUnit => businessUnit.accountIds?.includes(accountId));
    const toBusinessUnit = Object.values(props.businessUnitEntity || {})
      .find(businessUnit => businessUnit._id === combinedInstance.businessUnit?._id);
    if (toBusinessUnit?._id !== fromBusinessUnit?._id) {
      toBusinessUnit && props.replaceBusinessUnit(toBusinessUnit._id, {
        ...toBusinessUnit,
        accountIds: [...toBusinessUnit.accountIds, accountId]
      });
      fromBusinessUnit && props.replaceBusinessUnit(fromBusinessUnit._id, {
        ...fromBusinessUnit,
        accountIds: fromBusinessUnit.accountIds.filter(memberAccountId => memberAccountId !== accountId)
      });
    }
  },
  [props.businessUnitEntity, props.replaceBusinessUnit]);

  const replaceAccountInstance = React.useCallback((accountId, combinedInstance) => {
    const newUsername = commonUtils.getUsernameFromEmail(combinedInstance.email);
    // Double-check if 'newUsername' is unique.
    const hasExistingMatches = Object.values(props.accountEntity || {})
      .filter(accountInstance => accountInstance._id !== accountId)
      .some(accountInstance => accountInstance.username === newUsername);
    if (hasExistingMatches) {
      throw new Error(`De usernaam van e-mail adres '${combinedInstance.email}' wordt al gebruikt door een ander CVtool account.`);
    }
    const account = Object.values(props.accountEntity || {})
      .find(accountInstance => accountInstance._id === accountId);
    props.replaceAccount(account._id, {
      ...account,
      name: combinedInstance.name,
      email: combinedInstance.email,
      username: newUsername
    });
  },
  [props.accountEntity, props.replaceAccount]);

  const replaceAuthorizationInstance = React.useCallback((accountId, combinedInstance) => {
    const authorization = Object.values(props.authorizationEntity || {})
      .find(authorizationInstance => authorizationInstance.accountId === accountId);
    props.replaceAuthorization(authorization._id, {
      ...authorization,
      level: combinedInstance.authorization.level
    });
  },
  [props.authorizationEntity, props.replaceAuthorization]);

  const combined = React.useMemo(() => {
    const combinedEntity = combineEntities(props.accountEntity, props.authorizationEntity, props.brandEntity, props.businessUnitEntity);
    return {
      entity: combinedEntity,
      instances:Object.values(combinedEntity || {})
    };
  },
  [props.accountEntity, props.authorizationEntity, props.brandEntity, props.businessUnitEntity]);

  const combinedContext = React.useCallback(replaceInstance => ({
    entity: combined.entity,
    instanceId: props.selectedAccountId,
    setSelectedInstanceId: props.setSelectedAccountId,
    replaceInstance: replaceInstance
  }),
  [combined.entity, props.selectedAccountId]);

  const BusinessUnitOptions = React.useMemo(() => {
    const options = Object.values(props.businessUnitEntity || {})
      .filter(businessUnit => businessUnit._id) // Don't show deleted businessUnits.
      .sort((l, r) => commonUtils.comparePrimitives(l.name, r.name))
      .map((businessUnit, index) => ({
        key: businessUnit._id,
        sortIndex: index + 1,
        text: {
          nl_NL: businessUnit.name
        }
      }));
    return [
      {
        key: null,
        sortIndex: 0,
        text: {}
      },
      ...options
    ];
  },
  [props.businessUnitEntity]);

  const onRenderAuthorization = item =>
    enums.getText(enums.Authorizations, item.authorization?.level, props.locale);

  const columns = [
    {
      key: "name",
      fieldName: "name",
      name: "Naam",
      isResizable: true,
      minWidth: 110,
      maxWidth: 220
    },
    {
      key: "businessUnit.name",
      fieldName: "businessUnit.name",
      name: "Unit",
      isResizable: true,
      minWidth: 120
    },
    {
      key: "brand",
      fieldName: "brand.name",
      name: "Label",
      isResizable: true,
      minWidth: 120,
      maxWidth: 150
    },
    {
      key: "authorization.level.",
      fieldName: "authorization.level.",
      onRender: onRenderAuthorization,
      name: "Autorisatie",
      isResizable: false,
      minWidth: 70,
      maxWidth: 70
    }
  ];

  const [filterText, setFilterText] = React.useState("");

  const items = React.useMemo(() => {
    let newItems = combined.instances;
    if (filterText) {
      const lowerCaseFilterText = filterText.toLowerCase();
      newItems = combined.instances.filter(instance =>
        `${instance.name}\n${instance.brand?.name || ""}\n${instance.businessUnit?.name || ""}\n${instance.email || ""}`
          .toLowerCase().includes(lowerCaseFilterText));
    }
    return newItems;
  },
  [combined.instances, filterText]);

  const onFilter = (_, newFilterText) =>
    setFilterText(newFilterText);

  const onFetchCv = () => {
    if (props.selectedAccountId && props.selectedAccountId !== props.authInfo.accountId) {
      props.fetchCvByAccountId(props.selectedAccountId);
    }
  };

  const onFetchCvReport = () =>
    props.fetchCvReport();

  const [confirmDialogVisible, setConfirmDialogVisible] = React.useState(false);
  const selectedItemFields = React.useCallback(() => {
    const selectedAccount = combined.instances.find(businessUnit => businessUnit._id === props.selectedAccountId);
    return selectedAccount && {
      Naam: selectedAccount.name,
      "E-mail": selectedAccount.email,
      Unit: selectedAccount.businessUnit?.name
    };
  }, [combined.instances, props.selectedAccountId]);

  const isFilledAccount = account =>
    account.name !== DEFAULT_ACCOUNT_NAME && account.email !== DEFAULT_ACCOUNT_EMAIL;

  const onAddAccount = () => {
    let newAccount = combined.instances.find(account => !isFilledAccount(account));
    if (!newAccount) {
      newAccount = {
        _id: commonUtils.createUuid(),
        name: DEFAULT_ACCOUNT_NAME,
        email: DEFAULT_ACCOUNT_EMAIL
      };
      props.replaceAccount(newAccount._id, newAccount);
      const newAuthorization = {
        _id: commonUtils.createUuid(),
        accountId: newAccount._id,
        level: enums.Authorizations[0].key
      };
      props.replaceAuthorization(newAuthorization._id, newAuthorization);
    }
    props.setSelectedAccountId(newAccount._id);
  };

  const onDeleteAccount = () => {
    if (props.selectedAccountId && props.selectedAccountId !== props.authInfo.accountId) {
      setConfirmDialogVisible(true);
    }
  };
  const onDeleteConfirmed = () => {
    setConfirmDialogVisible(false);
    if (props.selectedAccountId) {
      props.deleteAccount(props.selectedAccountId);
      props.setSelectedAccountId(undefined);
    }
  };
  const onDeleteCancelled = () =>
    setConfirmDialogVisible(false);

  const {editPaneBackground, viewPaneBackground, semanticColors} = useTheme();
  const viewStyles = {
    root: {
      background: viewPaneBackground,
      padding: 20,
      minWidth: 550,
      height: "calc(100vh - 170px)"
    }
  };
  const editStyles = {
    root: {
      background: editPaneBackground,
      padding: 20,
      height: "calc(100vh - 170px)"
    }
  };
  const tdStyle = {
    width: "calc(50vw - 98px)"
  };

  const selectedAccountName = props.accountEntity?.[props.selectedAccountId]?.name;
  const cvAction = ["ADMIN", "UNIT_LEAD"].includes(props.authInfo.authorizationLevel) ? "bewerken" : "bekijken";

  return (
    <table style={{ borderCollapse: "collapse" }}>
      <tbody>
        <tr>
          <th valign="top" style={tdStyle}>
            <Stack styles={viewStyles}>
              <Stack horizontal horizontalAlign="space-between">
                <Text variant="xxLarge">
                  {["ADMIN", "UNIT_LEAD", "SALES"].includes(props.authInfo.authorizationLevel)
                    ? createHelpIcon({
                      label: "Accounts",
                      content:
                        <Text>
                          Deze lijst toont alleen de accountgegevens.
                          <br/><strong>Dubbel-klikken</strong> op een account haalt ook de cv-gegevens op.
                          <br/>De CV-menu items worden dan geënabled zodat je naar de details van het cv kunt navigeren.
                        </Text>
                    })
                    : "Accounts"
                  }
                </Text>
                <StackItem>
                  <Stack horizontal tokens={{ childrenGap: "s1" }}>
                    {["ADMIN"].includes(props.authInfo.authorizationLevel)
                      && <DefaultButton
                        text="Toevoegen"
                        iconProps={{ iconName: "Add" }}
                        onClick={onAddAccount}
                      />
                    }
                    <TextField
                      label="Filter"
                      iconProps={{ iconName: "Filter" }}
                      underlined
                      onChange={onFilter}
                    />
                    <Text variant="small">{items.length} / {combined.instances.length}</Text>
                    <ConfirmDialog
                      title="Account en cv-gegevens definitief verwijderen?"
                      primaryButtonText="Verwijderen"
                      selectedItemFields={selectedItemFields}
                      isVisible={confirmDialogVisible}
                      onProceed={onDeleteConfirmed}
                      onCancel={onDeleteCancelled}
                      styles={{ main: { backgroundColor: semanticColors.severeWarningBackground } }}
                    />
                  </Stack>
                </StackItem>
              </Stack>
              <CvDetailsList
                columns={columns}
                items={items}
                instanceContext={combinedContext()}
                setKey="accounts"
                onItemInvoked={onFetchCv}
              />
            </Stack>
          </th>

          <td valign="top" style={tdStyle}>
            {["ADMIN", "UNIT_LEAD", "SALES"].includes(props.authInfo.authorizationLevel)
              && <Stack styles={editStyles} verticalAlign="space-between">
                <Stack tokens={{ childrenGap: "s1" }}>
                  <CvTextField
                    label="Naam"
                    field="name"
                    instanceContext={combinedContext(replaceAccountInstance)}
                    readOnly={!["ADMIN", "UNIT_LEAD"].includes(props.authInfo.authorizationLevel)}
                  />
                  <CvTextField
                    label="E-mail"
                    field="email"
                    instanceContext={combinedContext(replaceAccountInstance)}
                    readOnly={!["ADMIN"].includes(props.authInfo.authorizationLevel)}
                    disabled={!props.selectedAccountId || props.selectedAccountId === props.authInfo.accountId}
                  />
                  <CvDropdown
                    label="Autorisatie"
                    field="authorization.level"
                    instanceContext={combinedContext(replaceAuthorizationInstance)}
                    readOnly={!["ADMIN"].includes(props.authInfo.authorizationLevel)}
                    disabled={!props.selectedAccountId || props.selectedAccountId === props.authInfo.accountId}
                    options={enums.getOptions(enums.Authorizations, props.locale)}
                    styles={{ dropdown: { width: 415 } }}
                  />
                  <CvDropdown
                    label="Unit"
                    field="businessUnit._id"
                    instanceContext={combinedContext(switchBusinessUnitOfAccount)}
                    readOnly={!["ADMIN", "UNIT_LEAD"].includes(props.authInfo.authorizationLevel)}
                    disabled={!["ADMIN"].includes(props.authInfo.authorizationLevel) && (!props.selectedAccountId || props.selectedAccountId === props.authInfo.accountId)}
                    options={enums.getOptions(BusinessUnitOptions, props.locale)}
                    styles={{ dropdown: { width: 415 } }}
                  />
                  <Separator/>
                  <TooltipHost content={`Haal de gegevens op om het CV te ${cvAction}`}>
                    <DefaultButton
                      text={`CV ${cvAction}`}
                      iconProps={{ iconName: "CloudDownload" }}
                      disabled={!props.selectedAccountId}
                      onClick={onFetchCv}
                      styles={{ root: { width: 230 } }}
                    />
                  </TooltipHost>
                  {["ADMIN", "UNIT_LEAD"].includes(props.authInfo.authorizationLevel)
                    && <TooltipHost content="Download status rapport van alle cv's">
                      <Separator/>
                      <DefaultButton
                        text="Download status rapport"
                        iconProps={{ iconName: "CloudDownload" }}
                        onClick={onFetchCvReport}
                        styles={{ root: { width: 230 } }}
                      />
                    </TooltipHost>
                  }
                </Stack>
                {["ADMIN"].includes(props.authInfo.authorizationLevel)
                  && <TooltipHost content={`Definitief verwijderen van alle gegevens${selectedAccountName
                    ? ` van ${selectedAccountName}`
                    : " van een account"}`}>
                    <DefaultButton
                      text="Account verwijderen"
                      iconProps={{ iconName: "Delete" }}
                      disabled={!props.selectedAccountId || props.selectedAccountId === props.authInfo.accountId}
                      onClick={onDeleteAccount}
                      styles={{ root: { width: 230, color: semanticColors.severeWarningIcon } }}
                    />
                  </TooltipHost>
                }
              </Stack>
            }
          </td>
        </tr>
      </tbody>
    </table>
  );
};

Accounts.propTypes = {
  locale: PropTypes.string,
  authInfo: PropTypes.object,
  accountEntity: PropTypes.object,
  authorizationEntity: PropTypes.object,
  brandEntity: PropTypes.object,
  businessUnitEntity: PropTypes.object,
  deleteAccount: PropTypes.func,
  replaceAccount: PropTypes.func,
  replaceAuthorization: PropTypes.func,
  replaceBusinessUnit: PropTypes.func,
  selectedAccountId: PropTypes.string,
  setSelectedAccountId: PropTypes.func,
  fetchCvByAccountId: PropTypes.func,
  fetchCvReport: PropTypes.func
};

export default Accounts;